import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import useSharedStyles from '../theme/pbdqShared.styles';
import { pbdqFont } from '../theme/pbdqTokens';
import { sdsColors } from '../theme/solutionsDesignSystem';
import pbdqRoutes from '../pbdqRoutes';
import { PBDQ_SOLUTIONS, PBDQ_SOLUTIONS_CONFIG } from '../constants/pbdqSolutions.constants';
import DqLogo from '../../../assets/images/PoweredByDQ/HomeHeroDqLogo.png';

const useNavDropdownStyles = makeStyles(() => ({
    dropdownWrap: {
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        height: '100%',
        '&:hover $dropdownMenu': {
            opacity: 1,
            visibility: 'visible',
            transform: 'translateY(0)',
            pointerEvents: 'auto',
        },
    },
    solutionsNavLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '26px 0 24px',
        borderBottom: '2px solid transparent',
        whiteSpace: 'nowrap',
        fontSize: 15,
        fontWeight: 500,
        color: '#1B2430',
        fontFamily: pbdqFont,
        cursor: 'pointer',
        transition: 'color 120ms',
        '&:hover': {
            color: sdsColors.accent,
        },
    },
    chevronIcon: {
        width: 14,
        height: 14,
        transition: 'transform 200ms ease',
        '$dropdownWrap:hover &': {
            transform: 'rotate(180deg)',
        },
    },
    dropdownMenu: {
        position: 'absolute',
        top: 'calc(100% - 14px)',
        left: 0,
        transform: 'translateY(4px)',
        width: 'max-content',
        background: '#FFFFFF',
        borderRadius: 8,
        boxShadow: '0 8px 24px -4px rgba(15, 28, 39, 0.1), 0 1px 3px rgba(15, 28, 39, 0.05)',
        border: '1px solid #EAECEF',
        padding: '4px 0',
        overflow: 'hidden',
        opacity: 0,
        visibility: 'hidden',
        pointerEvents: 'none',
        transition: 'opacity 140ms ease, transform 140ms ease, visibility 140ms',
        zIndex: 100,
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        // Invisible bridge to prevent hover loss between link and menu
        '&::before': {
            content: '""',
            position: 'absolute',
            top: -14,
            left: 0,
            right: 0,
            height: 16,
        },
    },
    dropdownItem: {
        display: 'flex !important',
        alignItems: 'center',
        gap: 9,
        padding: '10px 18px !important',
        margin: 0,
        borderRadius: 0,
        width: '100%',
        textDecoration: 'none !important',
        color: '#2C3540 !important',
        fontFamily: pbdqFont,
        fontSize: '13px !important',
        fontWeight: '500 !important',
        lineHeight: '1.2 !important',
        transition: 'background 100ms ease, color 100ms ease',
        cursor: 'pointer',
        borderBottom: 'none !important',
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
        '&:hover': {
            background: '#F5F7FA',
            color: `${sdsColors.accent} !important`,
        },
    },
    dropdownDot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        flexShrink: 0,
        opacity: 0.9,
    },
    dropdownItemTitle: {
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.2,
        color: 'inherit',
        whiteSpace: 'nowrap',
    },
    navMobileToggle: {
        display: 'none',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        border: 'none',
        background: 'transparent',
        borderRadius: 8,
        cursor: 'pointer',
        color: '#1B2430',
        padding: 0,
        transition: 'background 120ms, color 120ms',
        '&:hover': {
            background: '#F2F4F7',
            color: sdsColors.accent,
        },
        '@media (max-width: 960px)': {
            display: 'inline-flex',
        },
    },
    navMobileToggleIcon: {
        width: 24,
        height: 24,
    },
    mobileMenuOverlay: {
        position: 'fixed',
        top: 64,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        zIndex: 1099,
        display: 'none',
        '@media (max-width: 960px)': {
            display: 'block',
        },
    },
    mobileMenuContainer: {
        background: '#FFFFFF',
        borderBottom: '1px solid #E9ECF0',
        boxShadow: '0 12px 32px rgba(15, 28, 39, 0.12)',
        padding: '16px 20px 24px',
        maxHeight: 'calc(100vh - 64px)',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
    },
    mobileNavLink: {
        display: 'flex',
        alignItems: 'center',
        padding: '12px 14px',
        fontSize: 16,
        fontWeight: 500,
        color: '#1B2430',
        textDecoration: 'none',
        borderRadius: 8,
        fontFamily: pbdqFont,
        transition: 'background 120ms, color 120ms',
        '&:hover': {
            background: '#F5F7FA',
            color: sdsColors.accent,
        },
    },
    mobileSolutionsSection: {
        display: 'flex',
        flexDirection: 'column',
    },
    mobileSolutionsHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '12px 14px',
        fontSize: 16,
        fontWeight: 500,
        color: '#1B2430',
        background: 'transparent',
        border: 'none',
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: pbdqFont,
        textAlign: 'left',
        transition: 'background 120ms, color 120ms',
        '&:hover': {
            background: '#F5F7FA',
            color: sdsColors.accent,
        },
    },
    mobileChevron: {
        width: 16,
        height: 16,
        transition: 'transform 200ms ease',
    },
    mobileChevronRotated: {
        transform: 'rotate(180deg)',
    },
    mobileSolutionsList: {
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: 12,
        paddingTop: 4,
        paddingBottom: 4,
        gap: 4,
    },
    mobileSolutionItem: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        fontSize: 14,
        fontWeight: 500,
        color: '#2C3540',
        textDecoration: 'none',
        borderRadius: 6,
        fontFamily: pbdqFont,
        transition: 'background 120ms, color 120ms',
        '&:hover': {
            background: '#F5F7FA',
            color: `${sdsColors.accent} !important`,
        },
    },
    mobileDivider: {
        height: 1,
        background: '#E9ECF0',
        margin: '12px 0 16px',
        border: 'none',
    },
    mobileUtility: {
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        paddingTop: 4,
    },
    mobileLogin: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: 44,
        borderRadius: 999,
        border: '1px solid #D0D5DD',
        background: '#FFFFFF',
        color: '#1B2430',
        fontSize: 15,
        fontWeight: 600,
        textDecoration: 'none',
        fontFamily: pbdqFont,
        transition: 'background 120ms, border-color 120ms',
        '&:hover': {
            background: '#F9FAFB',
            borderColor: '#98A2B3',
        },
    },
    mobileGetStarted: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        height: 46,
        borderRadius: 999,
        background: '#1B2430',
        color: '#FFFFFF !important',
        fontSize: 15,
        fontWeight: 600,
        textDecoration: 'none',
        fontFamily: pbdqFont,
        transition: 'background 200ms ease',
        '&:hover': {
            background: '#2A3A4C',
        },
    },
}));

// The source highlights the current nav destination (color: var(--color-accent),
// aria-current="page"). "Solutions" stays highlighted across every solution
// sub-page (COS Assessments, School COS Readiness, LearnCreator, Workforce AI
// Readiness, Program Validation, DQEveryChild) — verified directly against the
// source mockup across all 13 pages, not a guess.
const SOLUTIONS_GROUP = [
    pbdqRoutes.solutions,
    pbdqRoutes.cosAssessments,
    pbdqRoutes.schoolCosReadiness,
    pbdqRoutes.learnCreator,
    pbdqRoutes.workforceAiReadiness,
    pbdqRoutes.programValidation,
    pbdqRoutes.dqEveryChild,
];

const ChevronDownIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m6 9 6 6 6-6" />
    </svg>
);

const ArrowRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14" />
        <path d="m12 5 7 7-7 7" />
    </svg>
);

const MenuIcon = ({ className }) => (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
);

const CloseIcon = ({ className }) => (
    <svg className={className} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);

const PbdqNav = () => {
    const classes = useSharedStyles();
    const dropdownClasses = useNavDropdownStyles();
    const { pathname, hash } = useLocation();

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [solutionsExpanded, setSolutionsExpanded] = useState(false);

    useEffect(() => {
        if (!hash) {
            window.scrollTo(0, 0);
        }
    }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    // Close mobile menu on route change
    useEffect(() => {
        setMobileMenuOpen(false);
    }, [pathname]);

    // Close mobile menu if window resized to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 960) {
                setMobileMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Prevent background scroll when mobile menu is open
    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const isActive = (paths) => paths.includes(pathname);
    const activeStyle = { color: sdsColors.accent, fontWeight: 600 };

    const solutionsActive = isActive(SOLUTIONS_GROUP);
    const capabilitiesActive = isActive([pbdqRoutes.capabilities]);
    const impactActive = isActive([pbdqRoutes.impact]);
    const partnersActive = isActive([pbdqRoutes.pricing]);
    const getStartedActive = isActive([pbdqRoutes.getStarted]);

    // Expand solutions accordion by default on mobile if current page is in solutions group
    useEffect(() => {
        if (solutionsActive) {
            setSolutionsExpanded(true);
        }
    }, [solutionsActive]);

    return (
        <>
            <header className={classes.navHeader}>
                <nav className={classes.nav} aria-label="Primary">
                    <span className={classes.navBrand}>
                        <Link
                            to={pbdqRoutes.home}
                            className={classes.navLogoLink}
                            onClick={() => setMobileMenuOpen(false)}
                        >
                            <span className={classes.navLogoText}>
                                Powered
                                <br />
                                by
                            </span>
                            <img src={DqLogo} alt="DQ" className={classes.navLogoImg} />
                        </Link>
                        <span className={classes.navSlogan}>
                            The engine behind
                            <br />
                            AI-digital literacy building
                        </span>
                    </span>
                    <div className={classes.navLinks}>
                        <div className={dropdownClasses.dropdownWrap}>
                            <Link
                                to={pbdqRoutes.solutions}
                                className={dropdownClasses.solutionsNavLink}
                                style={solutionsActive ? activeStyle : undefined}
                                aria-current={solutionsActive ? 'page' : undefined}
                            >
                                <span>Solutions</span>
                                <ChevronDownIcon className={dropdownClasses.chevronIcon} />
                            </Link>
                            <div className={dropdownClasses.dropdownMenu} role="menu" aria-label="Solutions menu">
                                {PBDQ_SOLUTIONS.map((s, idx) => {
                                    const cfg = PBDQ_SOLUTIONS_CONFIG[idx % PBDQ_SOLUTIONS_CONFIG.length];
                                    return (
                                        <Link
                                            key={s.index}
                                            to={s.to}
                                            role="menuitem"
                                            className={dropdownClasses.dropdownItem}
                                        >
                                            <span
                                                className={dropdownClasses.dropdownDot}
                                                style={{ background: cfg.bar }}
                                            />
                                            <span className={dropdownClasses.dropdownItemTitle}>
                                                {s.shortTitle || s.title}
                                            </span>
                                        </Link>
                                    );
                                })}
                                <Link
                                    to={pbdqRoutes.dqEveryChild}
                                    role="menuitem"
                                    className={dropdownClasses.dropdownItem}
                                >
                                    <span
                                        className={dropdownClasses.dropdownDot}
                                        style={{ background: '#052F44' }}
                                    />
                                    <span className={dropdownClasses.dropdownItemTitle}>
                                        #DQEveryChild
                                    </span>
                                </Link>
                            </div>
                        </div>
                        <Link
                            to={pbdqRoutes.capabilities}
                            style={capabilitiesActive ? activeStyle : undefined}
                            aria-current={capabilitiesActive ? 'page' : undefined}
                        >
                            Capabilities
                        </Link>
                        <Link
                            to={pbdqRoutes.impact}
                            style={impactActive ? activeStyle : undefined}
                            aria-current={impactActive ? 'page' : undefined}
                        >
                            Impact
                        </Link>
                        <Link
                            to={pbdqRoutes.pricing}
                            style={partnersActive ? activeStyle : undefined}
                            aria-current={partnersActive ? 'page' : undefined}
                        >
                            Partners
                        </Link>
                    </div>
                    <div className={classes.navUtility}>
                        <a href="https://app.poweredbydq.com/login" className={classes.navLogin}>Log in</a>
                        <Link
                            to={pbdqRoutes.getStarted}
                            className={classes.navGetStarted}
                            aria-current={getStartedActive ? 'page' : undefined}
                        >
                            Get started <ArrowRightIcon />
                        </Link>
                    </div>

                    {/* Mobile Hamburger Button */}
                    <button
                        type="button"
                        className={dropdownClasses.navMobileToggle}
                        aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                        aria-expanded={mobileMenuOpen}
                        onClick={() => setMobileMenuOpen((prev) => !prev)}
                    >
                        {mobileMenuOpen ? (
                            <CloseIcon className={dropdownClasses.navMobileToggleIcon} />
                        ) : (
                            <MenuIcon className={dropdownClasses.navMobileToggleIcon} />
                        )}
                    </button>
                </nav>
            </header>

            {/* Spacer below fixed navbar on mobile */}
            <div className={classes.navSpacer} />

            {/* Mobile Navigation Drawer */}
            {mobileMenuOpen && (
                <div
                    className={dropdownClasses.mobileMenuOverlay}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) {
                            setMobileMenuOpen(false);
                        }
                    }}
                >
                    <div className={dropdownClasses.mobileMenuContainer}>
                        <div className={dropdownClasses.mobileSolutionsSection}>
                            <button
                                type="button"
                                className={dropdownClasses.mobileSolutionsHeader}
                                onClick={() => setSolutionsExpanded((prev) => !prev)}
                            >
                                <span style={solutionsActive ? activeStyle : undefined}>Solutions</span>
                                <ChevronDownIcon
                                    className={clsx(
                                        dropdownClasses.mobileChevron,
                                        solutionsExpanded && dropdownClasses.mobileChevronRotated
                                    )}
                                />
                            </button>
                            {solutionsExpanded && (
                                <div className={dropdownClasses.mobileSolutionsList}>
                                    <Link
                                        to={pbdqRoutes.solutions}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={dropdownClasses.mobileSolutionItem}
                                        style={pathname === pbdqRoutes.solutions ? activeStyle : undefined}
                                    >
                                        <span
                                            className={dropdownClasses.dropdownDot}
                                            style={{ background: sdsColors.accent }}
                                        />
                                        <span>All Solutions</span>
                                    </Link>
                                    {PBDQ_SOLUTIONS.map((s, idx) => {
                                        const cfg = PBDQ_SOLUTIONS_CONFIG[idx % PBDQ_SOLUTIONS_CONFIG.length];
                                        const itemActive = pathname === s.to;
                                        return (
                                            <Link
                                                key={s.index}
                                                to={s.to}
                                                onClick={() => setMobileMenuOpen(false)}
                                                className={dropdownClasses.mobileSolutionItem}
                                                style={itemActive ? activeStyle : undefined}
                                            >
                                                <span
                                                    className={dropdownClasses.dropdownDot}
                                                    style={{ background: cfg.bar }}
                                                />
                                                <span>{s.shortTitle || s.title}</span>
                                            </Link>
                                        );
                                    })}
                                    <Link
                                        to={pbdqRoutes.dqEveryChild}
                                        onClick={() => setMobileMenuOpen(false)}
                                        className={dropdownClasses.mobileSolutionItem}
                                        style={pathname === pbdqRoutes.dqEveryChild ? activeStyle : undefined}
                                    >
                                        <span
                                            className={dropdownClasses.dropdownDot}
                                            style={{ background: '#052F44' }}
                                        />
                                        <span>#DQEveryChild</span>
                                    </Link>
                                </div>
                            )}
                        </div>

                        <Link
                            to={pbdqRoutes.capabilities}
                            onClick={() => setMobileMenuOpen(false)}
                            className={dropdownClasses.mobileNavLink}
                            style={capabilitiesActive ? activeStyle : undefined}
                        >
                            Capabilities
                        </Link>
                        <Link
                            to={pbdqRoutes.impact}
                            onClick={() => setMobileMenuOpen(false)}
                            className={dropdownClasses.mobileNavLink}
                            style={impactActive ? activeStyle : undefined}
                        >
                            Impact
                        </Link>
                        <Link
                            to={pbdqRoutes.pricing}
                            onClick={() => setMobileMenuOpen(false)}
                            className={dropdownClasses.mobileNavLink}
                            style={partnersActive ? activeStyle : undefined}
                        >
                            Partners
                        </Link>

                        <div className={dropdownClasses.mobileDivider} />

                        <div className={dropdownClasses.mobileUtility}>
                            <Link
                                to="/login"
                                onClick={() => setMobileMenuOpen(false)}
                                className={dropdownClasses.mobileLogin}
                            >
                                Log in
                            </Link>
                            <Link
                                to={pbdqRoutes.getStarted}
                                onClick={() => setMobileMenuOpen(false)}
                                className={dropdownClasses.mobileGetStarted}
                            >
                                Get started <ArrowRightIcon />
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PbdqNav;
